import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMask } from '@react-input/mask'

import { EMPTY_STRING } from '../../../constants/common'
import { FORMIK_EMPTY_VALUE } from '../../../constants/validation'
import { useConstant, useInitData } from '../../../hooks'
import { getIntlLocale } from '../../../utils/locale'
import { isFormikEmptyValue } from '../../../utils/validation'
import { AVATAR_HEIGHT_20, AVATAR_HEIGHT_24 } from '../../Avatar'
import {
  InputSelect,
  SelectDropdownListHeader,
  SelectDropdownListSearchInput,
} from '../../Select'
import { PHONE_COUNTRIES, PHONE_MASK_REPLACEMENT } from '../Phone.constants'
import {
  getPhoneCountryDisplayNames,
  getPhoneCountryOptions,
  parsePhoneValue,
  toCountrySelectItem,
} from '../Phone.utils'

import { PHONE_SELECT_DROPDOWN_FLOATING_CONFIG } from './PhoneSelect.constants'
import {
  filterPhoneSelectCountryOptions,
  getNextValue,
} from './PhoneSelect.utils'
import { PhoneSelectControlContent } from './PhoneSelectControlContent'
import { PhoneSelectDropdownEntityListItemContentInfoText } from './PhoneSelectDropdownEntityListItemContentInfoText'

import * as styles from './PhoneSelect.module.css'

function PhoneSelect({
  autoFocus,
  controlProps,
  dropdownFloatingConfig = PHONE_SELECT_DROPDOWN_FLOATING_CONFIG,
  emitOnCountrySelect = false,
  value,
  onChangeSearch,
  ...restProps
}) {
  const {
    i18n: { language },
  } = useTranslation()

  const {
    uk: {
      country: { code },
    },
  } = useInitData()

  const countryLocale = getIntlLocale(language)
  const countryDisplayNames = getPhoneCountryDisplayNames(countryLocale)

  const initialState = useConstant(() => {
    const parsed = parsePhoneValue(value, countryDisplayNames)

    if (parsed) {
      return parsed
    }

    const initialOption = PHONE_COUNTRIES.find(
      item => item[2] === code.toLowerCase()
    )

    return {
      country: toCountrySelectItem(initialOption, countryDisplayNames),
      inputValue: EMPTY_STRING,
    }
  })

  const [country, setCountry] = useState(initialState.country)
  const [inputValue, setInputValue] = useState(initialState.inputValue)

  const maskOptions = {
    mask: country.mask,
    replacement: PHONE_MASK_REPLACEMENT,
  }

  const inputRef = useMask(maskOptions)

  // Tracks the most recent string value this component itself emitted to the
  // outside (via onChangeSearch). When the incoming `value` prop differs from
  // it, the change came from outside (Formik resetForm / enableReinitialize /
  // draft restore after mount) and we need to re-parse it into local state.
  const lastEmittedRef = useRef(value)

  useEffect(() => {
    if (value !== lastEmittedRef.current) {
      const parsed = parsePhoneValue(value, countryDisplayNames)

      if (parsed) {
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setCountry(parsed.country)
        setInputValue(parsed.inputValue)
        lastEmittedRef.current = value
      } else if (!value) {
        // The value was cleared/reset from outside (Formik resetForm / draft
        // discard) — parsing returns `null`, so clear the local input to match.
        setInputValue(FORMIK_EMPTY_VALUE)
        lastEmittedRef.current = value
      }
    }
  }, [value])

  useEffect(() => {
    if (autoFocus) {
      inputRef.current?.focus()
    }
  }, [])

  function handleSelectItem(nextCountry) {
    setCountry(nextCountry)

    // Selecting a flag changes only local `country` state and normally never
    // reaches the form value (that happens via `onChangeSearch` on input).
    // When opted in, re-emit the value with the new country code so a
    // flag-only change is reflected in the form (e.g. edit "no changes" check).
    if (emitOnCountrySelect) {
      const nextEmitted = getNextValue({
        country: nextCountry,
        maskOptions,
        nextValue: inputValue,
      })

      lastEmittedRef.current = nextEmitted
      onChangeSearch(nextEmitted)
    }

    inputRef.current?.focus()
  }

  // This component should have self input value state for correct search work
  // it's because in sync select `searchValue` used in select control input.
  // This will lead to duplicating value in dropdown search input and control input.
  function handleChangeInput(event) {
    const nextValue = event.target.value

    setInputValue(nextValue)

    const nextEmitted = getNextValue({ country, maskOptions, nextValue })

    lastEmittedRef.current = nextEmitted
    onChangeSearch(nextEmitted)
  }

  function handleClear() {
    setInputValue(FORMIK_EMPTY_VALUE)

    lastEmittedRef.current = FORMIK_EMPTY_VALUE
    onChangeSearch(FORMIK_EMPTY_VALUE)
  }

  function handleClickContainer({ inputSelectRef }) {
    inputSelectRef.current?.focus()
  }

  function checkIsNeedToShowClearButton({ disabled }) {
    const hasSearchValue = inputValue && !isFormikEmptyValue(inputValue)

    return !disabled && hasSearchValue
  }

  const options = getPhoneCountryOptions({
    displayNames: countryDisplayNames,
    locale: countryLocale,
  })

  return (
    <InputSelect
      {...restProps}
      controlProps={{
        ...controlProps,
        Content: PhoneSelectControlContent,
        borderless: controlProps?.borderless ?? true,
        contentProps: {
          inputValue,
          onChange: handleChangeInput,
        },
        indicatorsProps: {
          ButtonArrow: null,
          checkIsNeedToShowClearButton,
        },
        inputRef,
        openDropdownOnClickContainer: false,
        onClickContainer: handleClickContainer,
      }}
      dropdownFloatingConfig={dropdownFloatingConfig}
      filter={filterPhoneSelectCountryOptions}
      listProps={{
        Header: SelectDropdownListHeader,
        headerProps: {
          Search: SelectDropdownListSearchInput,
        },
        itemProps: {
          contentProps: {
            avatarProps: {
              avatarClassName: styles.flagIcon,
              iconProps: {
                className: styles.iconContainer,
                svgSize: AVATAR_HEIGHT_20,
              },
              iconSize: AVATAR_HEIGHT_24,
              size: AVATAR_HEIGHT_24,
            },
            infoProps: {
              ContentText: PhoneSelectDropdownEntityListItemContentInfoText,
            },
          },
        },
      }}
      options={options}
      value={country}
      onClear={handleClear}
      onSelectItem={handleSelectItem}
    />
  )
}

export default PhoneSelect
